import type { RootStores } from './modules/common/stores';
import { STORE_RULES_IMPORT, STORE_RULES_IMPORT_RESOLUTION } from './modules/rules/constants';
import RulesImportResolutionStore from './modules/rules/stores/RulesImportResolutionStore';
import { TestProjectService } from './modules/test_projects/services';
import { injectTestProjectStores } from './modules/test_projects/stores';
import {
    STORE_TEST_PROJECT,
    STORE_TEST_PROJECT_INPUT_EDITOR,
    STORE_TEST_PROJECT_DASHBOARD
} from './modules/test_projects/stores/constants';
import type TestProjectDashboardStore from './modules/test_projects/stores/TestProjectDashboardStore';
import type TestProjectDataStore from './modules/test_projects/stores/TestProjectDataStore';
import type { TestProjectInputsChanged } from './modules/test_projects/types';
import type { TestProjectInputEditorSource } from './modules/test_projects/models/TestProjectInputEditorDraft';
import type { TestProjectChange } from './modules/common/types';

export function injectTestProjectsWithRuleImportResolution(stores: RootStores) {
    injectTestProjectStores(stores, (change, source) =>
        refreshTestProjectInputs(
            stores[STORE_RULES_IMPORT_RESOLUTION],
            stores[STORE_TEST_PROJECT_DASHBOARD],
            change,
            source
        )
    );

    // Connect the two features after their stores exist.
    const resolution = new RulesImportResolutionStore(
        stores[STORE_RULES_IMPORT],
        new TestProjectService(),
        stores[STORE_TEST_PROJECT],
        stores[STORE_TEST_PROJECT_INPUT_EDITOR]
    );
    stores[STORE_RULES_IMPORT_RESOLUTION] = resolution;
    stores[STORE_TEST_PROJECT].testProjectChangesSubject.subscribe((change: TestProjectChange) => {
        resolution.dataStore?.handleTestProjectChanges(change);
    });
}

export async function refreshTestProjectInputs(
    resolution: RulesImportResolutionStore,
    dashboard: TestProjectDashboardStore,
    change: TestProjectInputsChanged,
    source: TestProjectInputEditorSource
) {
    const owners = new Set<TestProjectDataStore>([source.dataStore]);
    const activeOwners = [dashboard.testProjectDataStore];

    // A closed or replaced import must not inherit a previous editor's saved outcome.
    if (source.isCurrent()) {
        activeOwners.push(resolution.dataStore);
    }

    for (const owner of activeOwners) {
        if (owner?.projectId === change.projectId && owner.testProjectId === change.testProjectId) {
            owners.add(owner);
        }
    }

    await Promise.all([
        ...Array.from(owners, owner => Promise.resolve().then(() => owner.handleInputsChanged(change))),
        Promise.resolve().then(() => resolution.handleInputsChanged(change))
    ]);
}
